/**
 * Decimal places used when rendering numbers into the LLM-facing tool
 * message. Declared as a const so the rounding policy can be tuned in
 * one place. Raw dataset values are never rounded — only the string
 * form that goes into the LLM context.
 */
export const LLM_MESSAGE_DECIMAL_PLACES = 2;

/**
 * Round a number to `LLM_MESSAGE_DECIMAL_PLACES` decimals for LLM-facing
 * display. Non-finite values (`NaN`, `±Infinity`) pass through unchanged
 * so callers can render them verbatim.
 *
 * The result is a plain number: `String(roundForDisplay(1.5))` yields
 * `"1.5"` (no trailing zeros).
 */
export function roundForDisplay(value: number): number {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** LLM_MESSAGE_DECIMAL_PLACES;
  return Math.round(value * factor) / factor;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

function roundValueForDisplay(value: unknown): unknown {
  if (typeof value === 'number') {
    return roundForDisplay(value);
  }
  if (Array.isArray(value)) {
    return value.map(roundValueForDisplay);
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = roundValueForDisplay(v);
    }
    return out;
  }
  return value;
}

/**
 * Return a deep-cloned row with numeric leaf values rounded for LLM
 * display. Recurses into nested plain objects and arrays; leaves Dates,
 * class instances, strings, booleans, and null unchanged. The original
 * row is never mutated, so the staged dataset keeps full precision.
 */
export function roundRowForDisplay(row: Record<string, unknown>): Record<string, unknown> {
  return roundValueForDisplay(row) as Record<string, unknown>;
}
